import React from 'react'
import { FormLabel } from '@mui/material'
import { t } from 'i18next'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import DynamicView from '../../../rsmCoreComponents/components/DynamicView/DynamicView'
import { useGetUserContactPreferenceDetailsQuery } from '../../../store/userService'
import { getUserInfo } from '../../../store/userInfo/userInfoSlice'

// eslint-disable-next-line @typescript-eslint/no-explicit-any, react/jsx-props-no-spreading
const FormLabelComponent = (props: any) => <FormLabel {...props} />

const ContactPreferenceView = () => {
  const userInfo = useSelector(getUserInfo)
  const { data } = useGetUserContactPreferenceDetailsQuery()
  const userProfileRedesignEnabled = useFeatureFlag('UserProfile_Redesign')

  // User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency
  // This is a temporary change and these fields will be restored in the future.
  const hideNotificationFrequencies = true

  const engFreqTxtKey =
    data?.data.engagementActivityNotificationFrequency?.frequencyTextKey
  const engagementFrequency = engFreqTxtKey
    ? `${t(
        `UserProfile.ContactPreferenceForm.NotificationFrequencies.${engFreqTxtKey}`,
      )}`
    : ''

  const insightFreqTxtKey =
    data?.data.insightsActivityNotificationFrequency?.frequencyTextKey
  const insightsFrequency = insightFreqTxtKey
    ? `${t(
        `UserProfile.ContactPreferenceForm.NotificationFrequencies.${insightFreqTxtKey}`,
      )}`
    : ''

  const timeZoneTxtKey = data?.data.timeZone?.timeZoneTextKey
  const regionOrTimeZone = timeZoneTxtKey
    ? `${t(`UserProfile.ContactPreferenceForm.TimeZones.${timeZoneTxtKey}`)}`
    : ''

  const contactMethodTxtKey = data?.data.preferredContactMethod?.contactMedium
  const contactMethod = contactMethodTxtKey
    ? `${t(
        `UserProfile.ContactPreferenceForm.ContactMethods.${contactMethodTxtKey}`,
      )}`
    : ''

  const clientBusinessNameValue = userInfo.isEmployee ? 'RSMUS' : 'RSMUS Client'

  const getFormattedBusinessPhone = () => {
    if (!data?.data.businessPhone || data?.data.businessPhone?.length <= 4)
      return ''

    return data?.data.businessPhone?.includes('(')
      ? data?.data.businessPhone
      : `(${data?.data.businessPhone?.slice(
          0,
          3,
        )}) ${data?.data.businessPhone?.slice(
          3,
          6,
        )}-${data?.data.businessPhone?.slice(6)}`
  }

  const formValues = {
    name:
      userInfo.firstName === undefined
        ? ''
        : `${userInfo.firstName} ${userInfo.lastName}`,
    businessEmail: userInfo.emailAddress ?? '',
    businessPhone: userProfileRedesignEnabled
      ? getFormattedBusinessPhone()
      : data?.data.businessPhone,
    clientBusinessName: userProfileRedesignEnabled
      ? data?.data.clientBusinessName
      : clientBusinessNameValue,
    preferredContactMethod: contactMethod,
    engagementActivityNotificationFrequency: engagementFrequency,
    insightsActivityNotificationFrequency: insightsFrequency,
    timeZone: regionOrTimeZone,
    visited: data?.data.visited,
  }

  const itemLayout = userProfileRedesignEnabled
    ? { item: true, lg: 6, md: 6, xs: 12 }
    : {
        item: true,
        lg: 4,
        md: 6,
        xs: 12,
      }
  const clientBusinessNameLabel = userProfileRedesignEnabled
    ? t('UserProfile.ContactPreferenceForm.ClientOrBusinessName')
    : t('UserProfile.ContactPreferenceForm.ClientOrBusinessNames')
  const businessPhoneLabel = userProfileRedesignEnabled
    ? t('UserProfile.ContactPreferenceForm.BusinessPhoneNumber')
    : t('UserProfile.ContactPreferenceForm.BusinessPhone')
  const businessEmailLabel = userProfileRedesignEnabled
    ? t('UserProfile.ContactPreferenceForm.Email')
    : t('UserProfile.ContactPreferenceForm.BusinessEmailAddress')

  return (
    <DynamicView
      containerLayout={
        userProfileRedesignEnabled
          ? { container: true, spacing: { xs: 1, sm: 2, md: 4, lg: 6 } }
          : {
              container: true,
              justifyContent: 'center',
              alignItems: 'center',
              spacing: { xs: 1, sm: 2, md: 4, lg: 6 },
            }
      }
      overrideFormControlProps={
        userProfileRedesignEnabled
          ? {
              sx: {
                my: 0,
              },
            }
          : undefined
      }
      definition={{
        name: {
          label: 'UserProfile.ContactPreferenceForm.Name',
          ariaLabel: 'UserProfile.ContactPreferenceForm.Name',
          component: FormLabelComponent,
          itemLayout,
        },
        ...((userProfileRedesignEnabled && !userInfo.isEmployee) ||
        !userProfileRedesignEnabled
          ? {
              clientBusinessName: {
                label: clientBusinessNameLabel,
                ariaLabel: clientBusinessNameLabel,
                component: FormLabelComponent,
                itemLayout,
              },
            }
          : {}),
        ...(userProfileRedesignEnabled
          ? {}
          : {
              timeZone: {
                label: 'UserProfile.ContactPreferenceForm.RegionOrTimeZone',
                ariaLabel: 'UserProfile.ContactPreferenceForm.RegionOrTimeZone',
                component: FormLabelComponent,
                itemLayout,
              },
            }),
        businessPhone: {
          label: businessPhoneLabel,
          ariaLabel: businessPhoneLabel,
          component: FormLabelComponent,
          itemLayout,
        },
        businessEmail: {
          label: businessEmailLabel,
          ariaLabel: businessEmailLabel,
          component: FormLabelComponent,
          itemLayout,
        },
        preferredContactMethod: {
          label: 'UserProfile.ContactPreferenceForm.PreferredContactMethod',
          ariaLabel: 'UserProfile.ContactPreferenceForm.PreferredContactMethod',
          component: FormLabelComponent,
          itemLayout,
        },
        /* User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency */
        ...(hideNotificationFrequencies
          ? {}
          : {
              engagementActivityNotificationFrequency: {
                label:
                  'UserProfile.ContactPreferenceForm.EngagementActivityNotificationFrequency',
                ariaLabel:
                  'UserProfile.ContactPreferenceForm.EngagementActivityNotificationFrequency',
                component: FormLabelComponent,
                itemLayout,
              },
              insightsActivityNotificationFrequency: {
                label:
                  'UserProfile.ContactPreferenceForm.InsightsActivityNotificationFrequency',
                ariaLabel:
                  'UserProfile.ContactPreferenceForm.InsightsActivityNotificationFrequency',
                component: FormLabelComponent,
                itemLayout,
              },
            }),
      }}
      initialValues={formValues}
    />
  )
}

export default ContactPreferenceView
